import { useState, useEffect } from 'react'
import { useAuth } from '../../shared/auth'
import { useNavigate } from 'react-router-dom'
import { inquiryService } from '../services/inquiryService'
import toast from 'react-hot-toast'
import apiClient from '../../shared/auth/services'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useApiError } from '../../shared/hooks'
import { MessageDialog } from '../../shared/ui'
import Layout from '../components/Layout'

// Zod schema for inquiry form validation
const inquirySchema = z.object({
  name: z.string().min(1, '名前は必須項目です'),
  email: z.email('有効なメールアドレスを入力してください'),
  subject: z.string().min(1, '件名は必須項目です'),
  content: z.string().min(1, '内容は必須項目です')
})

const Inquiry = () => {
  const { isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingUserData, setIsLoadingUserData] = useState(false)
  // Use the error handling hook
  const {
    showErrorDialog,
    errorMessage,
    handleApiError,
    handleApiResponse,
    handleValidationErrors,
    closeErrorDialog
  } = useApiError(() => setIsSubmitting(false))

  // Use React Hook Form with Zod resolver
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(inquirySchema),
    mode: 'onSubmit'
  })

  // Initialize form with user data if logged in
  useEffect(() => {
    const fetchUserInfo = async () => {
      // Only fetch user info if user is authenticated
      if (!isAuthenticated) {
        return;
      }
      
      setIsLoadingUserData(true)
      try {
        const response = await apiClient.post('/inquiry', {});
        if (response.data.userName !== null) {
          setValue('name', response.data.userName || '')
          setValue('email', response.data.mailAddress || '')
        }
      } catch (error) {
        console.error('Failed to fetch user info:', error);
        // Don't show error dialog for user data fetch failure, just log it
        // The form can still be used without pre-filled data
      } finally {
        setIsLoadingUserData(false)
      }
    };
    
    // Only call fetchUserInfo if authentication is not loading
    if (!isLoading) {
      fetchUserInfo();
    }
  }, [isAuthenticated, isLoading, setValue])

  // Handle form submission
  const onSubmit = async (data) => {
    setIsSubmitting(true)

    try {
      const response = await inquiryService.sendInquiry(data)

      if (response.returnCode === '200') {
        toast.success(response.message || 'メール送信が完了しました。')
        // Clear form after successful submission
        setValue('subject', '')
        setValue('content', '')
      } else {
        handleApiResponse(response, 'エラーが発生しました。')
      }
    } catch (error) {
      console.error('Inquiry submission error:', error)
      if (error.response?.status === 406) {
        toast.error('ログインが必要です')
        navigate('/login')
      } else {
        handleApiError(error, 'エラーが発生しました。しばらく時間をおいて再度お試しください。')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle validation errors
  const onError = () => {
    handleValidationErrors('必須項目が入力されていません。入力を確認してください。')
  }

  const handleLoginClick = () => {
    navigate('/login')
  }

  // Simple utility to get border class based on error state and disabled state
  const getBorderClass = (fieldName, baseClass, isDisabled = false) => {
    if (errors[fieldName]) {
      return `${baseClass} border-red-500 bg-red-50`
    }
    if (isDisabled) {
      return `${baseClass} border-gray-300 bg-gray-100 text-gray-500`
    }
    return `${baseClass} border-gray-300 bg-gray-50`
  }

  const formContent = (
    <form onSubmit={handleSubmit(onSubmit, onError)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
        <label htmlFor="name" className="md:col-span-1 bg-gray-100 px-4 py-3 border border-gray-300 font-medium text-gray-700">
          名前 *
        </label>
        <div className="md:col-span-3">
          <div className="relative">
            <input
              id="name"
              type="text"
              {...register('name')}
              className={getBorderClass('name', 'w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500', isAuthenticated)}
              placeholder={isAuthenticated ? "ユーザー情報を読み込み中..." : "お名前を入力してください"}
              disabled={isAuthenticated || isLoadingUserData}
            />
            {isAuthenticated && isLoadingUserData && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" role="status" aria-label="ユーザー情報読み込み中"></div>
              </div>
            )}
          </div>
          <p className="mt-1 text-sm text-red-600">{errors.name ? errors.name.message : '\u00A0'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
        <label htmlFor="email" className="md:col-span-1 bg-gray-100 px-4 py-3 border border-gray-300 font-medium text-gray-700">
          メールアドレス *
        </label>
        <div className="md:col-span-3">
          <div className="relative">
            <input
              id="email"
              type="text"
              {...register('email')}
              className={getBorderClass('email', 'w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500', isAuthenticated)}
              placeholder={isAuthenticated ? "ユーザー情報を読み込み中..." : "メールアドレスを入力してください"}
              disabled={isAuthenticated || isLoadingUserData}
            />
            {isAuthenticated && isLoadingUserData && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" role="status" aria-label="ユーザー情報読み込み中"></div>
              </div>
            )}
          </div>
          <p className="mt-1 text-sm text-red-600">{errors.email ? errors.email.message : '\u00A0'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
        <label htmlFor="subject" className="md:col-span-1 bg-gray-100 px-4 py-3 border border-gray-300 font-medium text-gray-700">
          件名 *
        </label>
        <div className="md:col-span-3">
          <input
            id="subject"
            type="text"
            {...register('subject')}
            className={getBorderClass('subject', 'w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500')}
            placeholder="件名を入力してください"
          />
          <p className="mt-1 text-sm text-red-600">{errors.subject ? errors.subject.message : '\u00A0'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
        <label htmlFor="content" className="md:col-span-1 bg-gray-100 px-4 py-3 border border-gray-300 font-medium text-gray-700">
          内容 *
        </label>
        <div className="md:col-span-3">
          <textarea
            id="content"
            {...register('content')}
            rows={8}
            className={getBorderClass('content', 'w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y')}
            placeholder="お問い合わせ内容を入力してください"
          />
          <p className="mt-1 text-sm text-red-600">{errors.content ? errors.content.message : '\u00A0'}</p>
        </div>
      </div>

      <div className="flex flex-col items-center gap-4 pt-6">
        <button
          type="submit"
          disabled={isSubmitting}
          className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-bold py-3 px-8 rounded-md transition-colors duration-200 flex items-center justify-center"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              送信中...
            </>
          ) : (
            '送信'
          )}
        </button>

        {!isAuthenticated && (
          <button
            type="button"
            onClick={handleLoginClick}
            className="bg-transparent border-none text-blue-600 hover:text-blue-800 font-medium py-3 px-8 rounded-md transition-colors duration-200 border border-blue-600 hover:border-blue-800"
          >
            ログイン
          </button>
        )}

        {/* Error Dialog */}
        <MessageDialog
          isOpen={showErrorDialog}
          onClose={closeErrorDialog}
          title="メッセージ"
          message={errorMessage}
          type="error"
        />
      </div>
    </form>
  )

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" role="status" aria-label="読み込み中"></div>
      </div>
    )
  }

  // Main content that's the same for both authenticated and non-authenticated users
  const mainContent = (
    <div className="flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="bg-gray-800 text-white px-6 py-3">
            <h1 className="text-xl font-semibold">お問い合わせ</h1>
          </div>

          <div className="p-8">
            <div className="mb-8 text-gray-700">
              <p>下記のフォームより問い合わせをお願いします。</p>
              <p>内容によっては回答にお時間をいただくこともございます。</p>
            </div>
            {formContent}
          </div>
        </div>
      </div>
    </div>
  )

  // If authenticated, wrap with header
  if (isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Layout pageTitle="お問い合わせ">
          {mainContent}
        </Layout>
      </div>
    )
  }

  // If not authenticated, show standalone page
  return mainContent
}

export default Inquiry
